import React, { useState, useRef, useEffect, useCallback } from "react";
import AnimationPlayer from "./AnimationPlayer";
import GeoGebraViewer from "./GeoGebraViewer";
import InteractiveViewer from "./InteractiveViewer";
import { confirmCreditCharge } from "../api/creditsApi";
import { cleanErrorMessage } from "../api";
import { renderManimPatch } from "../api/manimApi";
import { toAbsoluteRenderUrl } from "../utils/videoUrl";
import "./RefinementDrawer.css";

const RENDER_SERVER_URL = process.env.REACT_APP_RENDER_SERVER_URL;

/**
 * 智能识别用户微调指令中是否指明了某个特定片段/场景
 * 支持识别：
 * - 场景类名（如 Scene1, IntroScene 等）
 * - 中文序数（如“第1幕”、“第二幕”、“第一个场景”、“第3个片段”、“片段2”等）
 */
const detectTargetSceneFromInstruction = (text, scenes) => {
  if (!text || !Array.isArray(scenes) || scenes.length <= 1) return null;
  const trimmed = text.trim();

  // 1. 精确/词边界匹配场景名称（支持忽略大小写）
  for (const scene of scenes) {
    if (!scene) continue;
    const regex = new RegExp(`\\b${scene}\\b`, "i");
    if (regex.test(trimmed)) {
      return scene;
    }
  }

  // 2. 中文序数映射
  const chineseNumMap = {
    "一": 1, "1": 1,
    "二": 2, "两": 2, "2": 2,
    "三": 3, "3": 3,
    "四": 4, "4": 4,
    "五": 5, "5": 5,
    "六": 6, "6": 6,
    "七": 7, "7": 7,
    "八": 8, "8": 8,
    "九": 9, "9": 9,
    "十": 10, "10": 10,
  };

  const pattern = /(?:第\s*([0-9一二两三四五六七八九十]+)\s*(?:个)?\s*(?:片段|场景|幕|部分|段)|(?:片段|场景|scene)\s*([0-9一二两三四五六七八九十]+))/i;
  const match = trimmed.match(pattern);
  if (match) {
    const rawNum = (match[1] || match[2] || "").trim();
    const index = chineseNumMap[rawNum] || parseInt(rawNum, 10);
    if (index && index >= 1 && index <= scenes.length) {
      return scenes[index - 1];
    }
  }

  return null;
};

/**
 * RefinementDrawer - 持续性二次编辑微调抽屉组件
 *
 * 从右侧滑入，包含对话框和新结果预览，形成左右对比布局。
 * 支持多轮连续微调。
 * 当 sceneNames.length > 1 时，在输入框上方渲染「片段选择条」，
 * 用户点选某片段后提交走 renderManimPatch 片段渲染模式；
 * 未选择片段时走原有全量渲染逻辑（保持兼容）。
 */
const RefinementDrawer = ({
  isOpen,
  onClose,
  originalCode,        // 当前（最新）代码
  basePrompt = "",     // 原始提示词
  currentMode,         // 当前模式: manim | threejs | qq | geogebra | solve
  onRefineComplete,    // 微调完成后回调 (refinedCode) => void，用于外部更新原始展示
  onRefineResultDelivered, // 微调结果在前端成功产出/加载后回调
  onBeforeRefine,      // 提交前校验（如积分预检），返回 false 则中止
  streamRefineCode,    // 注入的API函数
  user,
  currentRenderError = null, // 主界面当前代码的真实 manim 报错（供微调 AI 参考修复）
  // 片段微调相关 props（由父组件通过 AnimationPlayer.onSegmentInfoChange 传入）
  segmentUrls = null,   // string[] | null — 各 scene 对应的本地 /media URL
  sceneNames = null,    // string[] | null — 各 scene 名称，顺序与 segmentUrls 对应
  baseRequestId = null, // string | null   — 原始批量渲染任务 ID
  onSelectedSceneChange = null, // 新增：当选中的片段发生变化时的回调
}) => {
  // 对话历史：{role: 'user'|'assistant', content: string, type: 'text'|'code'}
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  // 视频模式下，AI 返回代码并不代表本轮微调完成；必须等对应渲染结果
  // 实际加载后才解锁下一轮，避免多个 patch 共享旧 base_request_id。
  const pendingVideoResultIdRef = useRef(null);
  // 抽屉内嵌 AnimationPlayer 最新一次渲染失败的真实 manim 报错
  const [drawerRenderError, setDrawerRenderError] = useState(null);
  // 片段选择：用户点选的目标 scene 名称（null 表示走全量渲染）
  const [selectedScene, setSelectedScene] = useState(null);

  // 本地片段微调状态（当 props 为空，或者抽屉内渲染成功产生新片段时，使用本地状态）
  const [localSegmentUrls, setLocalSegmentUrls] = useState(segmentUrls);
  const [localSceneNames, setLocalSceneNames] = useState(sceneNames);
  const [localBaseRequestId, setLocalBaseRequestId] = useState(baseRequestId);

  // 同步选中的场景状态到父组件
  useEffect(() => {
    if (onSelectedSceneChange) {
      if (!isOpen) {
        onSelectedSceneChange(null, null);
        return;
      }
      let videoUrl = null;
      if (selectedScene && Array.isArray(localSceneNames) && Array.isArray(localSegmentUrls)) {
        const idx = localSceneNames.indexOf(selectedScene);
        if (idx !== -1 && localSegmentUrls[idx]) {
          videoUrl = toAbsoluteRenderUrl(localSegmentUrls[idx], RENDER_SERVER_URL);
        }
      }
      onSelectedSceneChange(selectedScene, videoUrl);
    }
  }, [isOpen, selectedScene, localSceneNames, localSegmentUrls, onSelectedSceneChange]);

  // 同步外部 props 变化
  useEffect(() => {
    setLocalSegmentUrls(segmentUrls);
    setLocalSceneNames(sceneNames);
    setLocalBaseRequestId(baseRequestId);
  }, [segmentUrls, sceneNames, baseRequestId]);

  // 当片段列表变化时，重置当前选中的片段
  useEffect(() => {
    setSelectedScene(null);
  }, [localSceneNames]);

  // 找出最新的一条结果消息的 ID，用于限制只有最新一次微调的播放器才能触发状态同步
  const resultMessages = messages.filter((m) => m.type === "result");
  const latestResultId = resultMessages.length > 0 ? resultMessages[resultMessages.length - 1].id : null;
  // const [refinedCode, setRefinedCode] = useState(null);   // 最新一次微调结果代码 (已弃用，直接存储在消息中)
  // const [showResult, setShowResult] = useState(false);    // 是否展示结果预览区 (已弃用)
  // const [resultKey, setResultKey] = useState(0);          // 强制刷新结果预览 (已弃用)

  // 当前有效代码（每次微调成功后更新，用于下一轮的 originalCode）
  const currentCodeRef = useRef(originalCode);

  const drawerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const latestCombinedPromptRef = useRef("");

  // 视口与折叠屏/移动端键盘适配：当软键盘弹出或折叠屏状态变化时动态适配抽屉高度
  useEffect(() => {
    if (!isOpen) return;

    const handleVisualViewportChange = () => {
      if (window.visualViewport && drawerRef.current) {
        const { height, offsetTop } = window.visualViewport;
        drawerRef.current.style.setProperty('--vv-height', `${height}px`);
        drawerRef.current.style.setProperty('--vv-top', `${offsetTop}px`);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
      handleVisualViewportChange();
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, [isOpen]);

  const buildRefinedPrompt = useCallback((instruction) => {
    const base = String(basePrompt || "").trim();
    const refineInstruction = String(instruction || "").trim();
    if (!base) return refineInstruction;
    if (!refineInstruction) return base;
    if (base === refineInstruction) return base;
    return `${base}\n\n微调要求：${refineInstruction}`;
  }, [basePrompt]);

  // 同步外部 originalCode 变化（例如用户重新生成了作品）
  useEffect(() => {
    currentCodeRef.current = originalCode;
    // 当原始代码变化时重置抽屉内状态
    if (!isOpen) {
      setMessages([]);
    }
  }, [originalCode, isOpen]);

  // 控制 body class 以便降低其他固定元素的层级
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("refine-drawer-open");
    } else {
      document.body.classList.remove("refine-drawer-open");
    }
    return () => {
      document.body.classList.remove("refine-drawer-open");
    };
  }, [isOpen]);

  // 打开时聚焦输入框
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // 自动滚动到最新消息
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const appendMessage = (role, content, type = "text", code = null, mode = null, prompt = null, chargeSession = null, refineMeta = null, image = null) => {
    const message = {
      role,
      content,
      type,
      code,
      mode: mode || currentMode,
      prompt,
      chargeSession,
      refineMeta,
      image,
      id: Date.now() + Math.random(),
    };
    setMessages((prev) => [...prev, message]);
    return message.id;
  };

  const processImageFile = useCallback((file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("请上传图片文件");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("图片大小不能超过 5MB");
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.includes("image")) {
        const file = items[i].getAsFile();
        processImageFile(file);
        e.preventDefault();
        break;
      }
    }
  }, [processImageFile]);

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 是否处于片段微调模式（manim/manimgx/solve 且有多片段且用户已选择某片段）
  const isPatchMode = useCallback(() => {
    return (
      (currentMode === "manim" || currentMode === "manimgx" || currentMode === "solve") &&
      selectedScene !== null &&
      localBaseRequestId !== null &&
      Array.isArray(localSceneNames) &&
      localSceneNames.length > 1
    );
  }, [currentMode, selectedScene, localBaseRequestId, localSceneNames]);

  const handleSubmit = useCallback(async () => {
    const instruction = inputText.trim();
    if (!instruction || isRefining) return;
    const attachedImageFile = imageFile;
    const attachedImagePreview = imagePreview;

    if (onBeforeRefine) {
      const canRefine = await onBeforeRefine({ instruction, mode: currentMode, imageFile: attachedImageFile });
      if (!canRefine) return;
    }

    setInputText("");
    removeImage();
    setIsRefining(true);

    latestCombinedPromptRef.current = buildRefinedPrompt(instruction);

    // 智能推断目标片段：若用户未显式点选卡片，但指令中指明了具体片段，自动切换为片段微调模式
    let effectiveTargetScene = selectedScene;
    let isAutoDetectedScene = false;
    if (
      !effectiveTargetScene &&
      (currentMode === "manim" || currentMode === "manimgx" || currentMode === "solve") &&
      localBaseRequestId !== null &&
      Array.isArray(localSceneNames) &&
      localSceneNames.length > 1
    ) {
      const inferred = detectTargetSceneFromInstruction(instruction, localSceneNames);
      if (inferred) {
        effectiveTargetScene = inferred;
        isAutoDetectedScene = true;
        setSelectedScene(inferred);
      }
    }

    const runPatch = (
      (currentMode === "manim" || currentMode === "manimgx" || currentMode === "solve") &&
      effectiveTargetScene !== null &&
      localBaseRequestId !== null &&
      Array.isArray(localSceneNames) &&
      localSceneNames.length > 1
    );

    // ── 片段微调路径：仅重渲该场景，其余场景保持不变并自动拼接 ──
    if (runPatch) {
      const patchTag = isAutoDetectedScene
        ? `[智能定位片段：${effectiveTargetScene}]`
        : `[片段微调：${effectiveTargetScene}]`;
      appendMessage("user", `${patchTag} ${instruction}`, "text", null, null, null, null, null, attachedImagePreview);

      const renderErrorForRefine = drawerRenderError || currentRenderError || null;
      // 增强局部微调约束提示，避免 AI 破坏其他 Scene 结构
      const patchInstruction = `${instruction}\n\n【局部微调约束】：本任务为针对「${effectiveTargetScene}」的局部微调渲染。请务必重点修改 class ${effectiveTargetScene} 内部的代码或其直接关联逻辑，严禁修改、删除或重命名其他 Scene 类，保持其余场景代码完全不变，以便系统仅渲染该片段并自动与其余片段快速合并。`;

      try {
        await streamRefineCode(
          currentCodeRef.current,
          patchInstruction,
          currentMode,
          [], // 采用单轮微调模式，不传递对话历史，避免代码累加并支持用户手动修改
          null,
          async (newCode, fullResponse, chargeSession) => {
            try {
              const resp = await renderManimPatch(
                localBaseRequestId,
                effectiveTargetScene,
                newCode,
                "m",
                {
                  created_by: user?.id,
                  skip_showcase: true,
                  create_showcase: false,
                  mode: currentMode,
                  title: basePrompt ? `${basePrompt.substring(0, 40)} (微调)` : undefined,
                  prompt: latestCombinedPromptRef.current,
                }
              );
              if (!resp?.request_id) throw new Error("服务器未返回 request_id");

              // 更新当前有效代码，供下一轮微调使用
              currentCodeRef.current = newCode;

              setDrawerRenderError(null);

              const resultMessageId = appendMessage(
                "assistant",
                `✅ 片段「${effectiveTargetScene}」微调渲染中（仅重渲此片段并自动合并），效果如下：`,
                "result",
                newCode,
                currentMode,
                latestCombinedPromptRef.current,
                chargeSession,
                {
                  instruction,
                  mergedPrompt: latestCombinedPromptRef.current,
                  basePrompt,
                  mode: currentMode,
                  patchRequestId: resp.request_id,
                  patchScene: effectiveTargetScene,
                }
              );
              pendingVideoResultIdRef.current = resultMessageId;
            } catch (e) {
              setIsRefining(false);
              appendMessage("assistant", `❌ 片段渲染失败：${cleanErrorMessage(e.message)}，请稍后重试。`);
            }
          },
          (errMsg) => {
            setIsRefining(false);
            appendMessage("assistant", `❌ 微调失败：${cleanErrorMessage(errMsg)}，请稍后重试。`);
          },
          attachedImageFile,
          renderErrorForRefine
        );
      } catch (e) {
        setIsRefining(false);
        appendMessage("assistant", `❌ 网络错误：${cleanErrorMessage(e.message)}`);
      }
      return;
    }

    // ── 全量微调路径（未指定单一片段，或全局修改需求）──
    appendMessage("user", instruction, "text", null, null, null, null, null, attachedImagePreview);

    // 优先使用抽屉内嵌播放器最新的渲染错误（上一次微调失败的报错），
    // 退而使用主界面传入的初始渲染错误，都没有则为 null
    const renderErrorForRefine = drawerRenderError || currentRenderError || null;

    try {
      await streamRefineCode(
        currentCodeRef.current,
        instruction,
        currentMode,
        [], // 采用单轮微调模式，不传递对话历史，避免代码累加并支持用户手动修改
        // onChunk - 流式接收时可做进度反馈，这里不做细粒度处理
        null,
        // onComplete
        async (code, fullResponse, chargeSession) => {
          const isVideoMode = currentMode === "manim" || currentMode === "manimgx" || currentMode === "solve";
          if (!isVideoMode) {
            setIsRefining(false);
          }

          // 更新当前有效代码，以便下一轮微调基于最新结果
          currentCodeRef.current = code;
          // 代码已更新，清除之前记录的渲染错误，等待新的渲染结果
          setDrawerRenderError(null);

          // 【关键修复】对于 threejs/qq/geogebra，代码本身就是交付物，
          // handleAutoSave 也在此时调用，作品已入库。立即确认扣费，使扣费与入库保持同步。
          // 对 manim/manimgx/solve 不在此处扣费：视频才是交付物，渲染失败时不应扣费，
          // 由 AnimationPlayer 在视频加载成功后确认（原有逻辑保持不变）。
          // viewer 若再次调用 confirmCreditCharge，后端返回 alreadyConfirmed，不会重复扣费。
          const isCodeDeliverable = currentMode !== "manim" && currentMode !== "manimgx" && currentMode !== "solve";
          if (chargeSession?.sessionId && isCodeDeliverable) {
            try {
              await confirmCreditCharge({
                sessionId: chargeSession.sessionId,
                resultType: "refine",
                resultRef: null,
              });
            } catch (e) {
              // 静默处理：确认失败不阻塞展示结果，viewer 仍会再次尝试
              console.warn("微调扣费确认失败（将依赖 viewer 再次尝试）:", e?.message);
            }
          }

          // 通知父组件可以"采用"新代码（可选）
          if (onRefineComplete) {
            onRefineComplete(code, {
              instruction,
              mergedPrompt: latestCombinedPromptRef.current,
              basePrompt,
              mode: currentMode,
            });
          }

          const successText = isVideoMode
            ? "✅ 微调渲染中，效果如下："
            : "✅ 微调完成，效果如下：";

          // 将结果代码直接作为消息的一部分显示
          const resultMessageId = appendMessage(
            "assistant",
            successText,
            "result",
            code,
            currentMode,
            latestCombinedPromptRef.current,
            chargeSession,
            {
              instruction,
              mergedPrompt: latestCombinedPromptRef.current,
              basePrompt,
              mode: currentMode,
            }
          );
          if (isVideoMode) {
            pendingVideoResultIdRef.current = resultMessageId;
          }

          if (currentMode === "geogebra" && onRefineResultDelivered) {
            onRefineResultDelivered({
              code,
              mode: currentMode,
              prompt: latestCombinedPromptRef.current,
              chargeSession,
              renderResult: { commands: code },
              instruction,
              mergedPrompt: latestCombinedPromptRef.current,
              basePrompt,
            });
          }
        },
        // onError
        (errMsg) => {
          setIsRefining(false);
          appendMessage("assistant", `❌ 微调失败：${cleanErrorMessage(errMsg)}，请稍后重试。`);
        },
        attachedImageFile,
        renderErrorForRefine  // 附带的真实 manim 报错，让 AI 同步修复
      );
    } catch (e) {
      setIsRefining(false);
      appendMessage("assistant", `❌ 网络错误：${cleanErrorMessage(e.message)}`);
    }
  }, [inputText, isRefining, imageFile, imagePreview, currentMode, streamRefineCode, onRefineComplete, onRefineResultDelivered, onBeforeRefine, buildRefinedPrompt, basePrompt, drawerRenderError, currentRenderError, isPatchMode, selectedScene, localBaseRequestId, localSceneNames, user]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClose = useCallback(() => {
    if (isRefining) return; // 正在生成中禁止关闭
    onClose();
  }, [isRefining, onClose]);

  // ESC 键快捷关闭抽屉
  useEffect(() => {
    if (!isOpen) return;
    const handleEscKey = (e) => {
      if (e.key === "Escape" && !isRefining) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, [isOpen, isRefining, handleClose]);

  // 根据模式渲染结果预览
  const renderResultPreview = (code, msgId, mode, promptText = null, chargeSession = null, refineMeta = null) => {
    if (!code) return null;
    
    const targetMode = mode || currentMode;
    const isLatest = msgId === latestResultId;

    const notifyDelivered = (renderResult = null, confirmResult = null) => {
      if (!isLatest) {
        console.log("忽略非最新微调结果的回调:", msgId);
        return;
      }
      if (
        pendingVideoResultIdRef.current &&
        pendingVideoResultIdRef.current !== msgId
      ) {
        return;
      }
      if (Array.isArray(renderResult?.scene_names) && Array.isArray(renderResult?.segment_urls)) {
        setLocalSceneNames(renderResult.scene_names);
        setLocalSegmentUrls(renderResult.segment_urls);
        setLocalBaseRequestId(renderResult.request_id || null);
      }
      pendingVideoResultIdRef.current = null;
      setIsRefining(false);
      if (!onRefineResultDelivered) return;
      onRefineResultDelivered({
        code,
        mode: targetMode,
        prompt: promptText,
        chargeSession,
        renderResult,
        confirmResult,
        ...(refineMeta || {}),
      });
    };

    if (targetMode === "manim" || targetMode === "manimgx" || targetMode === "solve") {
      // 片段微调：AnimationPlayer 用 patchRequestId 接管已排队任务（通过 restoredRenderResult 触发轮询）
      const patchRequestId = refineMeta?.patchRequestId || null;
      const restoredResult = patchRequestId
        ? { success: false, status: "processing", request_id: patchRequestId }
        : null;
      return (
        <div className="refine-result-embedded">
          <AnimationPlayer
            key={msgId}
            code={code}
            isLoading={false}
            error={null}
            mode={targetMode === "solve" ? "solve" : (targetMode === "manimgx" ? "manimgx" : "manim")}
            prompt={promptText || buildRefinedPrompt("") || "微调结果"}
            title={patchRequestId ? `片段「${refineMeta?.patchScene || ""}」微调` : "微调预览"}
            created_by={user?.id}
            chargeSession={chargeSession}
            restoredRenderResult={restoredResult}
            onFinalResultDelivered={isLatest ? notifyDelivered : null}
            showRenderError={true}
            renderErrorHint="渲染遇到了问题，可在下方重新描述需求，AI 将尝试修复并重新生成。"
            onRenderError={(_displayErr, manimErr) => {
              if (isLatest && _displayErr) {
                // 记录真实的 manim 报错，供下一轮微调时注入给 AI
                setDrawerRenderError(manimErr || null);
                pendingVideoResultIdRef.current = null;
                setIsRefining(false);
              }
            }}
            onSegmentInfoChange={isLatest ? (info) => {
              if (info) {
                setLocalSegmentUrls(info.segmentUrls);
                setLocalSceneNames(info.sceneNames);
                setLocalBaseRequestId(info.requestId);
              }
            } : null}
          />
        </div>
      );
    }

    if (targetMode === "threejs" || targetMode === "qq") {
      return (
        <div className="refine-result-embedded refine-iframe-embedded">
          <InteractiveViewer
            key={msgId}
            htmlCode={code}
            isVisible={true}
            isEmbedded={true}
            allowDownload={true}
            chargeSession={chargeSession}
            onFinalResultDelivered={isLatest ? notifyDelivered : null}
          />
        </div>
      );
    }

    if (targetMode === "geogebra") {
      return (
        <div className="refine-result-embedded refine-geogebra-embedded">
          <GeoGebraViewer
            key={msgId}
            commands={code}
            isVisible={true}
            isEmbedded={true}
            allowDownload={true}
            chargeSession={chargeSession}
            onFinalResultDelivered={isLatest ? notifyDelivered : null}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {/* 遮罩层（在移动端/折叠屏下点击可关闭抽屉） */}
      {isOpen && <div className="refine-backdrop" onClick={isRefining ? undefined : handleClose} />}

      {/* 抽屉主体 */}
      <div ref={drawerRef} className={`refine-drawer ${isOpen ? "open" : ""}`}>
        {/* 抽屉头部 */}
        <div className="refine-drawer-header">
          <div className="refine-drawer-title">
            <i className="ri-magic-line"></i>
            <span>微调助手</span>
            <span className="refine-drawer-subtitle">在现有作品基础上持续优化</span>
          </div>
          <button
            className="refine-close-btn"
            onClick={handleClose}
            disabled={isRefining}
            title={isRefining ? "生成中，请稍候..." : "关闭"}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* 抽屉内容区：聊天 */}
        <div className="refine-drawer-body">
          {/* 对话历史区 */}
          <div className="refine-chat-history">
            {messages.length === 0 && (
              <div className="refine-chat-empty">
                <i className="ri-lightbulb-line"></i>
                <p>告诉我你想如何改进这个作品</p>
                <p className="refine-chat-hint">例如："把标题改为蓝色"、"加快动画速度"、"添加一个结尾总结"</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`refine-message refine-message-${msg.role} ${msg.type === 'result' ? 'refine-message-result' : ''}`}
              >
                <div className="refine-message-avatar">
                  {msg.role === "user" ? (
                    <i className="ri-user-line"></i>
                  ) : (
                    <i className="ri-robot-line"></i>
                  )}
                </div>
                <div className="refine-message-content-wrapper">
                  <div className="refine-message-content">
                     {msg.content}
                  </div>
                  {msg.image && (
                    <div className="refine-message-image">
                      <img src={msg.image} alt="微调参考图" />
                    </div>
                  )}
                  {msg.type === 'result' && msg.code && (
                    <div className="refine-result-container">
                       {renderResultPreview(msg.code, msg.id, msg.mode, msg.prompt, msg.chargeSession, msg.refineMeta)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* 生成中动画 */}
            {isRefining && (
              <div className="refine-message refine-message-assistant">
                <div className="refine-message-avatar">
                  <i className="ri-robot-line"></i>
                </div>
                <div className="refine-message-content refine-loading-msg">
                  <span className="refine-dot"></span>
                  <span className="refine-dot"></span>
                  <span className="refine-dot"></span>
                  <span className="refine-loading-text">正在微调中，请稍候...</span>
                </div>
              </div>
            )}
          </div>
          
          <div ref={messagesEndRef} />
        </div>

        {/* 片段选择条：仅在 manim/manimgx/solve 模式且有多个 scene 时显示 */}
        {(currentMode === "manim" || currentMode === "manimgx" || currentMode === "solve") &&
          Array.isArray(localSceneNames) &&
          localSceneNames.length > 1 && (
          <div className="refine-segment-bar">
            <span className="refine-segment-label">片段选择（点击选择要修改的场景）：</span>
            <div className="refine-segment-cards">
              <div
                className={`refine-segment-card refine-segment-card-all ${selectedScene === null ? "active" : ""} ${isRefining ? "disabled" : ""}`}
                onClick={() => !isRefining && setSelectedScene(null)}
              >
                <div className="refine-segment-card-preview-placeholder">
                  <i className="ri-movie-2-line"></i>
                </div>
                <div className="refine-segment-card-info">
                  <span className="refine-segment-card-title">全部片段</span>
                  <span className="refine-segment-card-desc">全量微调并重渲</span>
                </div>
              </div>
              {localSceneNames.map((scene, idx) => {
                const videoUrl = localSegmentUrls && localSegmentUrls[idx]
                  ? toAbsoluteRenderUrl(localSegmentUrls[idx], RENDER_SERVER_URL)
                  : null;
                return (
                  <div
                    key={scene}
                    className={`refine-segment-card ${!videoUrl ? "failed" : ""} ${selectedScene === scene ? "active" : ""} ${isRefining ? "disabled" : ""}`}
                    onClick={() => !isRefining && setSelectedScene(scene === selectedScene ? null : scene)}
                  >
                    <div className="refine-segment-card-preview">
                      {videoUrl ? (
                        <video
                          src={videoUrl}
                          muted
                          loop
                          playsInline
                          autoPlay
                          className="refine-segment-video-preview"
                        />
                      ) : (
                        <div className="refine-segment-card-preview-placeholder">
                          <i className="ri-film-line"></i>
                        </div>
                      )}
                      <span className="refine-segment-card-badge">{idx + 1}</span>
                    </div>
                    <div className="refine-segment-card-info">
                      <span className="refine-segment-card-title" title={scene}>{scene}</span>
                      <span className="refine-segment-card-desc">
                        {videoUrl ? "已渲染" : "渲染失败，点击修复"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedScene ? (
              <span className="refine-segment-hint">
                ⚡ 已选定片段「{selectedScene}」：系统将仅修改并重渲该片段，其余片段保持不变并自动拼接。
              </span>
            ) : (
              <span className="refine-segment-hint">
                💡 提示：点击具体片段卡片可进行单片段秒级重渲与自动合并；或直接在指令中输入“修改第X幕/片段X”。
              </span>
            )}
          </div>
        )}

        {/* 输入区 */}
        <div className="refine-input-area">
          <div className="refine-input-box">
            {imagePreview && (
              <div className="refine-image-preview">
                <img src={imagePreview} alt="微调参考图预览" />
                <button
                  type="button"
                  className="refine-remove-image-btn"
                  onClick={removeImage}
                  disabled={isRefining}
                  title="移除图片"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}
            <div className="refine-input-row">
              <button
                type="button"
                className={`refine-upload-btn ${imageFile ? "has-file" : ""}`}
                onClick={triggerFileUpload}
                disabled={isRefining}
                title="上传图片"
              >
                <i className="ri-image-line"></i>
                {imageFile && <span className="refine-upload-indicator"></span>}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
                accept="image/*"
              />
              <textarea
                ref={textareaRef}
                className="refine-textarea"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                  }, 250);
                }}
                placeholder="描述你的微调需求，可上传或粘贴参考图… (Enter 发送，Shift+Enter 换行)"
                rows={3}
                disabled={isRefining}
              />
            </div>
          </div>
          <button
            className="refine-send-btn"
            onClick={handleSubmit}
            disabled={isRefining || !inputText.trim()}
          >
            {isRefining ? (
              <>
                <i className="ri-loader-4-line rotating"></i>
                微调中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-2-line"></i>
                发送
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default RefinementDrawer;
